import { useEffect, useMemo, useState } from "react";
import { ShareNetwork, Copy, FilePdf, Trash, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { SnapshotView } from "@/components/SnapshotView";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/fers";
import { buildSnapshot } from "@/lib/snapshot";
import { exportSnapshotPdf } from "@/lib/snapshotPdf";

const linkOf = (token) => `${window.location.origin}/shared/snapshot/${token}`;

function LinkControls({ share, busy, onCreate, onRevoke }) {
  const copy = async () => { await navigator.clipboard.writeText(linkOf(share.token)).catch(() => {}); toast.success("Link copied"); };
  if (!share) return <Button className="rounded-full" onClick={onCreate} disabled={busy} data-testid="snapshot-create-link-button"><LinkSimple size={16} className="mr-1.5" />{busy ? "Creating..." : "Create share link"}</Button>;
  return (
    <div className="space-y-2 w-full" data-testid="snapshot-link-box">
      <div className="flex gap-2">
        <input readOnly value={linkOf(share.token)} className="flex-1 min-w-0 rounded-full border border-input bg-background px-3 text-xs font-mono-num" data-testid="snapshot-link-input" />
        <Button size="sm" variant="outline" className="rounded-full" onClick={copy} data-testid="snapshot-copy-link-button"><Copy size={14} className="mr-1" />Copy</Button>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="snapshot-link-expiry">Active until {fmtDate(new Date(share.expires_at))}. Anyone with the link can view this snapshot.</p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="rounded-full" onClick={onCreate} disabled={busy} data-testid="snapshot-refresh-link-button">New link with latest numbers</Button>
        <Button size="sm" variant="ghost" className="rounded-full text-red-600" onClick={onRevoke} disabled={busy} data-testid="snapshot-revoke-link-button"><Trash size={14} className="mr-1" />Turn off link</Button>
      </div>
    </div>
  );
}

export function ShareSnapshot({ profile, totals }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState(false);
  const data = useMemo(() => (open && profile ? buildSnapshot(profile, totals) : null), [open, profile, totals]);
  useEffect(() => { if (open) api.get("/snapshot/share").then((r) => setShare(r.data.share)).catch(() => {}); }, [open]);
  const run = async (fn, msg) => { setBusy(true); try { const r = await fn(); setShare(r.data.share); toast.success(msg); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const create = () => run(() => api.post("/snapshot/share", data), "Share link ready");
  const revoke = () => run(() => api.delete("/snapshot/share"), "Link turned off");
  const first = (user?.name || "").split(" ")[0];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="rounded-full" disabled={!profile} data-testid="share-snapshot-button"><ShareNetwork size={16} className="mr-1.5" />Share snapshot</Button></DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="share-snapshot-dialog">
        <DialogHeader>
          <DialogTitle>Share your retirement snapshot</DialogTitle>
          <DialogDescription>A one-page, read-only summary for a spouse or advisor. The link shows these numbers as of today and stops working after 30 days.</DialogDescription>
        </DialogHeader>
        {data && <SnapshotView d={data} />}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-start pt-2">
          <LinkControls share={share} busy={busy} onCreate={create} onRevoke={revoke} />
          {data && <Button variant="outline" className="rounded-full shrink-0" onClick={() => exportSnapshotPdf(data, first)} data-testid="snapshot-download-pdf-button"><FilePdf size={16} className="mr-1.5" />Download PDF</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
